import { useEffect, useMemo, useState } from 'react';
import { type LastEntry, api } from '../api';
import { weekdayOf } from '../dates';
import { categoryOf, type ExerciseDef } from '../exercises';
import { type Praise, pickPraise, scoreOf, thresholdsFrom } from '../praise';
import {
  type DraftEntry,
  type EntryKind,
  type Workout,
  UNIT_OPTIONS,
  newDraft,
  toDraft,
  toEntries,
  unitLabel,
  volumeOf,
} from '../types';
import { ExercisePicker } from './ExercisePicker';
import { Icon } from './Icon';
import { Sheet } from './Sheet';

type Props = {
  date: string;
  onClose: () => void;
  onSaved: (date: string, workout: Workout | null, praise: Praise | null) => void;
  onError: (err: unknown) => void;
};

export function DayEditor({ date, onClose, onSaved, onError }: Props) {
  const [drafts, setDrafts] = useState<DraftEntry[]>([]);
  const [bodyWeight, setBodyWeight] = useState('');
  const [bodyFat, setBodyFat] = useState('');
  const [note, setNote] = useState('');
  const [existed, setExisted] = useState(false);
  const [lastByExercise, setLastByExercise] = useState<Record<string, LastEntry>>({});
  const [recentScores, setRecentScores] = useState<number[]>([]);
  /** 前回の計測値。プレースホルダに出して目安にする。 */
  const [lastBody, setLastBody] = useState<{ weight?: string; fat?: string }>({});
  const [picking, setPicking] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const [workout, prefill] = await Promise.all([api.getWorkout(date), api.getPrefill(date)]);
        if (cancelled) return;

        setLastByExercise(prefill.lastByExercise);
        setRecentScores(prefill.recentWorkouts.map((w) => scoreOf(w.entries)));
        setLastBody({
          weight: prefill.recentWorkouts.find((w) => w.bodyWeight !== undefined)?.bodyWeight?.toString(),
          fat: prefill.recentWorkouts.find((w) => w.bodyFat !== undefined)?.bodyFat?.toString(),
        });

        if (workout) {
          // 日単位のコピーはしない。器具を選んだ時点でその器具の前回値が入る。
          setDrafts(workout.entries.map((e) => toDraft(e)));
          setBodyWeight(workout.bodyWeight !== undefined ? String(workout.bodyWeight) : '');
          setBodyFat(workout.bodyFat !== undefined ? String(workout.bodyFat) : '');
          setNote(workout.note ?? '');
          setExisted(true);
        } else {
          setDrafts([]);
          // 体重は前回の計測値をプレースホルダで示す (勝手には入れない)
          setBodyWeight('');
          setBodyFat('');
          setNote('');
          setExisted(false);
        }
      } catch (err) {
        if (!cancelled) onError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [date, onError]);

  const thresholds = useMemo(() => thresholdsFrom(recentScores), [recentScores]);

  const update = (id: string, patch: Partial<DraftEntry>) =>
    setDrafts((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const remove = (id: string) => setDrafts((rows) => rows.filter((r) => r.id !== id));

  /** 器具を選んだら、その器具の前回の重さ・回数・セット数をそのまま初期値にする */
  const addExercise = (def: ExerciseDef) => {
    const last = lastByExercise[def.name];
    setDrafts((rows) => [
      ...rows,
      last
        ? { ...toDraft(last, last.date), kind: def.kind, id: crypto.randomUUID() }
        : newDraft({ exercise: def.name, kind: def.kind, sets: def.kind === 'cardio' ? '1' : '1' }),
    ]);
    setPicking(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const entries = toEntries(drafts);
      const optional = (v: string) => (v.trim() === '' ? undefined : Number(v));
      const workout = await api.saveWorkout(date, {
        entries,
        bodyWeight: optional(bodyWeight),
        bodyFat: optional(bodyFat),
        note: note.trim() || undefined,
      });
      // 体組成だけ記録した日は褒めない (トレーニングしていないため)
      onSaved(date, workout, entries.length > 0 ? pickPraise(entries, thresholds) : null);
      onClose();
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  const removeAll = async () => {
    if (!confirm(`${date} の記録を削除します。よろしいですか？`)) return;
    setSaving(true);
    try {
      await api.deleteWorkout(date);
      onSaved(date, null, null);
      onClose();
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  const totalVolume = volumeOf(toEntries(drafts));
  const customNames = Object.keys(lastByExercise);

  return (
    <>
      <Sheet
        title={
          <div className="sheet__title">
            <span className="sheet__date">{date.slice(5).replace('-', ' / ')}</span>
            <span className="sheet__wd">
              {weekdayOf(date)}曜日
              {totalVolume > 0 && ` · ${Math.round(totalVolume).toLocaleString('ja-JP')} kg`}
            </span>
          </div>
        }
        onClose={onClose}
        footer={
          <>
            <button className="btn btn--danger" onClick={removeAll} disabled={!existed || saving}>
              削除
            </button>
            <button className="btn btn--primary" onClick={save} disabled={loading || saving}>
              {saving ? <span className="spinner" /> : '保存する'}
            </button>
          </>
        }
      >
        {loading ? (
          <div className="empty">
            <span className="spinner" style={{ display: 'inline-block' }} />
          </div>
        ) : (
          <>
            {/* 体組成はトレーニングの有無に関係なく記録できる */}
            <div className="body-metrics">
              <div className="numfield">
                <span className="label">体重</span>
                <div className="numfield__row">
                  <input
                    className="field field--num"
                    inputMode="decimal"
                    value={bodyWeight}
                    placeholder={lastBody.weight ?? '—'}
                    onChange={(e) => setBodyWeight(e.target.value)}
                  />
                  <span className="numfield__unit">kg</span>
                </div>
              </div>
              <div className="numfield">
                <span className="label">体脂肪率</span>
                <div className="numfield__row">
                  <input
                    className="field field--num"
                    inputMode="decimal"
                    value={bodyFat}
                    placeholder={lastBody.fat ?? '—'}
                    onChange={(e) => setBodyFat(e.target.value)}
                  />
                  <span className="numfield__unit">%</span>
                </div>
              </div>
            </div>

            <div className="entries">
              {drafts.map((row) => (
                <EntryRow
                  key={row.id}
                  row={row}
                  onChange={(patch) => update(row.id, patch)}
                  onRemove={() => remove(row.id)}
                />
              ))}
            </div>

            {drafts.length === 0 && (
              <div className="empty">
                下のボタンから器具を選んでください。
                <br />
                前回その器具で入力した重さ・回数・セット数が自動で入ります。
              </div>
            )}

            <button
              className="btn btn--primary btn--wide"
              style={{ marginTop: drafts.length > 0 ? 12 : 4 }}
              onClick={() => setPicking(true)}
            >
              <Icon name="plus" size={15} />
              器具を選ぶ
            </button>

            <textarea
              className="note"
              value={note}
              placeholder="メモ (体調、フォームの気づき など)"
              onChange={(e) => setNote(e.target.value)}
            />

            <p className="hint">
              トレーニングしていない日でも、体重と体脂肪率だけ記録できます。
              種目も体組成もすべて空にして保存すると、その日は「記録なし」になります。
            </p>
          </>
        )}
      </Sheet>

      {picking && (
        <ExercisePicker
          customNames={customNames}
          lastByExercise={lastByExercise}
          onPick={addExercise}
          onClose={() => setPicking(false)}
        />
      )}
    </>
  );
}

// ------------------------------------------------------------------ 1 行分

const KIND_LABEL: Record<EntryKind, string> = {
  weight: '重量',
  reps: '回数',
  hold: '秒数',
  cardio: '有酸素',
};

function EntryRow({
  row,
  onChange,
  onRemove,
}: {
  row: DraftEntry;
  onChange: (patch: Partial<DraftEntry>) => void;
  onRemove: () => void;
}) {
  const category = categoryOf(row.exercise);

  /** 単位プルダウン付きの重さ欄 */
  const weightField = () => (
    <div className="numfield">
      <span className="label">重さ</span>
      <div className="numfield__row">
        <input
          className="field field--num"
          inputMode="decimal"
          value={row.weight}
          placeholder="0"
          onChange={(e) => onChange({ weight: e.target.value })}
        />
        <select
          className="unitsel"
          value={row.unit}
          aria-label="重さの単位"
          onChange={(e) => onChange({ unit: e.target.value as DraftEntry['unit'] })}
        >
          {UNIT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  /** 1 ずつ増減できる欄 (回数・セット数)。下限は 0、セット数だけ 1。 */
  const stepField = (
    label: string,
    key: 'reps' | 'sets',
    placeholder: string,
    min: number,
  ) => {
    const bump = (delta: number) => {
      const next = Math.max(min, Math.round(Number(row[key]) || 0) + delta);
      onChange({ [key]: String(next) });
    };
    return (
      <div className="numfield">
        <span className="label">{label}</span>
        <div className="stepper">
          <button
            className="stepper__btn"
            onClick={() => bump(-1)}
            aria-label={`${label}を1減らす`}
            disabled={(Number(row[key]) || 0) <= min}
          >
            <Icon name="minus" size={15} />
          </button>
          <input
            className="field field--num stepper__input"
            inputMode="numeric"
            value={row[key]}
            placeholder={placeholder}
            onChange={(e) => onChange({ [key]: e.target.value })}
          />
          <button className="stepper__btn" onClick={() => bump(1)} aria-label={`${label}を1増やす`}>
            <Icon name="plus" size={15} />
          </button>
        </div>
      </div>
    );
  };

  /** 増減ボタンの要らない欄 (秒・分・km) */
  const plainField = (
    label: string,
    key: 'seconds' | 'minutes' | 'distanceKm',
    unit: string,
    placeholder: string,
    mode: 'decimal' | 'numeric' = 'numeric',
  ) => (
    <div className="numfield">
      <span className="label">{label}</span>
      <div className="numfield__row">
        <input
          className="field field--num"
          inputMode={mode}
          value={row[key]}
          placeholder={placeholder}
          onChange={(e) => onChange({ [key]: e.target.value })}
        />
        <span className="numfield__unit">{unit}</span>
      </div>
    </div>
  );

  return (
    <div className="entry">
      <div className="entry__top">
        <div className="entry__name">
          <span className="entry__exercise">{row.exercise}</span>
          <span className="entry__tags">
            {category && <span className="entry__tag">{category}</span>}
            <span className={`pick__kind pick__kind--${row.kind}`}>{KIND_LABEL[row.kind]}</span>
            {row.carriedFrom && (
              <span className="entry__carry">
                <Icon name="carry" size={11} />
                前回 {row.carriedFrom.slice(5)}
              </span>
            )}
          </span>
        </div>
        <button className="entry__remove" onClick={onRemove} aria-label={`${row.exercise} を削除`}>
          <Icon name="minus" />
        </button>
      </div>

      {/* 測り方ごとに入力欄を変える */}
      {row.kind === 'cardio' ? (
        <div className="entry__nums entry__nums--2">
          {plainField('時間', 'minutes', '分', '30', 'decimal')}
          {plainField('距離', 'distanceKm', 'km', '5.0', 'decimal')}
        </div>
      ) : row.kind === 'hold' ? (
        <div className="entry__nums entry__nums--2">
          {plainField('保持', 'seconds', '秒', '60')}
          {stepField('セット', 'sets', '1', 1)}
        </div>
      ) : row.kind === 'reps' ? (
        <div className="entry__nums entry__nums--2">
          {stepField('回数', 'reps', '10', 0)}
          {stepField('セット', 'sets', '1', 1)}
        </div>
      ) : (
        /* 重量種目は増減ボタンの幅を確保するため、重さを 1 行使って上に置く */
        <>
          <div className="entry__nums entry__nums--1">{weightField()}</div>
          <div className="entry__nums entry__nums--2">
            {stepField('回数', 'reps', '10', 0)}
            {stepField('セット', 'sets', '1', 1)}
          </div>
        </>
      )}

      <RowSummary row={row} />
    </div>
  );
}

function RowSummary({ row }: { row: DraftEntry }) {
  const n = (v: string) => Number(v) || 0;

  if (row.kind === 'cardio') {
    const min = n(row.minutes);
    const km = n(row.distanceKm);
    if (min === 0 && km === 0) return null;
    const pace = min > 0 && km > 0 ? ` · ${(min / km).toFixed(1)} 分/km` : '';
    return <div className="entry__volume">{`${min} 分 / ${km} km${pace}`}</div>;
  }

  if (row.kind === 'hold') {
    const total = n(row.seconds) * Math.max(1, n(row.sets));
    return total > 0 ? <div className="entry__volume">合計 {total} 秒</div> : null;
  }

  if (row.kind === 'reps') {
    const total = n(row.reps) * Math.max(1, n(row.sets));
    return total > 0 ? <div className="entry__volume">合計 {total} レップ</div> : null;
  }

  const vol = n(row.weight) * n(row.reps) * Math.max(1, n(row.sets));
  return vol > 0 ? (
    <div className="entry__volume">
      VOL {Math.round(vol).toLocaleString('ja-JP')} {unitLabel(row.unit)}
    </div>
  ) : null;
}
